import { useEffect, useState, type ReactNode } from 'react';
import {
  Cable,
  Cloud,
  Download,
  History,
  Link2,
  MonitorSmartphone,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Smartphone,
  Wifi,
} from 'lucide-react';
import {
  BRANCH_API_STATE_LABELS,
  type BusinessDetailsView,
  type LiveBranchRow,
  type SectionAvailability,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { CountryDisplay } from '@/components/location';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DescriptionList,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Modal,
  SearchInput,
  SecretField,
  StatusBadge,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import {
  createProductionBranch,
  listBranchInventory,
  revealBranchEnrollment,
  updateProductionBranch,
  updateProductionTenant,
} from '@/data';
import {
  BranchApiManager,
  countConfiguredBranches,
  keysForBranch,
} from '@/features/pharmacies/api/BranchApiManager';
import { CloudPosBadge } from '@/features/pharmacies/components/CloudPosBadge';
import { DeviceLicenseManager } from '@/features/pharmacies/licenses/DeviceLicenseManager';
import { PharmacyLicenseManager } from '@/features/pharmacies/licenses/PharmacyLicenseManager';
import { OwnerUsersManager } from '@/features/pharmacies/owners/OwnerUsersManager';
import { PosControlManager } from '@/features/pharmacies/pos-control/PosControlManager';
import { ENTITY_TYPE_LABELS, SYSTEM_LABELS, maskId } from '@/features/production-linking/labels';
import { errorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';
import type { BusinessWorkspaceTab } from './BusinessWorkspaceNav';

function showTab(tab: BusinessWorkspaceTab | undefined, ...ids: BusinessWorkspaceTab[]) {
  return tab == null || ids.includes(tab);
}

function SectionState({ availability, message }: { availability: SectionAvailability; message?: string | null }) {
  if (availability === 'available') return null;
  const tone =
    availability === 'unavailable' ? 'warning' : availability === 'forbidden' ? 'neutral' : 'neutral';
  const label =
    availability === 'unavailable'
      ? message || 'Production data temporarily unavailable'
      : availability === 'not_applicable'
        ? message || 'Not applicable'
        : availability === 'not_linked'
          ? message || 'Not linked'
          : message || 'Not available for your role';
  return (
    <Badge tone={tone} className="mb-3">
      {label}
    </Badge>
  );
}

function SectionCard({
  title,
  icon,
  availability,
  message,
  children,
  description,
  actions,
}: {
  title: string;
  icon: ReactNode;
  availability: SectionAvailability;
  message?: string | null;
  children?: ReactNode;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <Card>
      <CardHeader title={title} icon={icon} description={description} actions={actions} />
      <CardBody>
        <SectionState availability={availability} message={message} />
        {availability === 'available' || (availability === 'not_linked' && children) || availability === 'not_applicable' ? (
          children
        ) : availability === 'unavailable' ? (
          <p className="text-sm text-ink-secondary">{message || 'Production data temporarily unavailable'}</p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function onlineTone(status: string | null | undefined): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'ONLINE') return 'success';
  if (status === 'STALE') return 'warning';
  if (status === 'OFFLINE') return 'danger';
  return 'neutral';
}

/** Live production sections for a Business Master (Phase 3B.3 + Batch C + Module 01). */
export function LiveBusinessDetailsPanels({
  details,
  loading,
  businessId,
  onRefresh,
  tab,
  onRequestTab,
  onSetUpCloudPos,
  hideLegacyBranchPanels = false,
}: {
  details: BusinessDetailsView | null;
  loading?: boolean;
  businessId?: string;
  onRefresh?: () => void;
  /** When set, only sections for this workspace tab are rendered. */
  tab?: BusinessWorkspaceTab;
  /** Navigate workspace tabs (e.g. Branches → Access & API). */
  onRequestTab?: (next: BusinessWorkspaceTab) => void;
  onSetUpCloudPos?: () => void;
  /** When true, Cloud branches panel is omitted (unified Branches tab owns that UX). */
  hideLegacyBranchPanels?: boolean;
}) {
  const can = useCan();
  const [enrollment, setEnrollment] = useState<{ branchId: string; code: string | null } | null>(null);
  const [enrollmentBusy, setEnrollmentBusy] = useState<string | null>(null);
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
  const [inventoryBranch, setInventoryBranch] = useState<LiveBranchRow | null>(null);
  const [editTenant, setEditTenant] = useState(false);
  const [editBranch, setEditBranch] = useState<LiveBranchRow | null>(null);
  const [addBranch, setAddBranch] = useState(false);
  const [focusBranchApiId, setFocusBranchApiId] = useState<string | null>(null);
  const [inlineApiBranchId, setInlineApiBranchId] = useState<string | null>(null);

  if (loading && !details) return <LoadingState className="py-16" label="Loading production details…" />;
  if (!details) return null;

  const { overview, connection, cloud, licensing, devices, updates, owners, api, identityHistory, activity } = details;
  const bizId = businessId ?? overview.id;
  const canEnroll = can('enrollment.manage');
  const canTenant = can('tenant.manage');
  const canInventory = can('sync.read') || can('production.read');
  const canAddBranch = can('tenant.manage');
  const canManageApi = can('api.manage');
  const productionAvailable = overview.productionAvailable === true;
  const configuredApiCount = countConfiguredBranches(
    cloud.branches.map((b) => b.id),
    api.keys,
  );

  const focusApi = (branchId: string) => {
    setFocusBranchApiId(branchId);
    setInlineApiBranchId(branchId);
    onRequestTab?.('access');
  };

  const revealEnrollment = async (branchId: string) => {
    setEnrollmentBusy(branchId);
    setEnrollmentError(null);
    try {
      const res = await revealBranchEnrollment(bizId, branchId, { idempotencyKey: crypto.randomUUID() });
      setEnrollment({ branchId, code: res.enrollmentCode });
    } catch (e) {
      setEnrollmentError(errorMessage(e));
    } finally {
      setEnrollmentBusy(null);
    }
  };

  return (
    <>
      {showTab(tab, 'overview') && (
      <Card className="xl:col-span-2">
        <CardHeader
          title="Connections"
          icon={<Link2 size={16} aria-hidden="true" />}
          actions={<CloudPosBadge status={overview.cloudPosStatus ?? 'not_connected'} />}
        />
        <CardBody>
          <SectionState availability={connection.availability} message={connection.message} />
          {overview.cloudPosStatus === 'not_connected' && onSetUpCloudPos && canTenant && (
            <div className="mb-4">
              <Button variant="secondary" size="sm" leftIcon={<Cloud size={14} aria-hidden="true" />} onClick={onSetUpCloudPos}>
                Set Up Cloud POS
              </Button>
            </div>
          )}
          {cloud.availability === 'available' && cloud.branches.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              <StatusBadge tone="info" label={`Cloud branches ${cloud.branches.length}`} dot={false} />
              {api.availability === 'available' && (
                <StatusBadge
                  tone={configuredApiCount === cloud.branches.length ? 'success' : 'neutral'}
                  label={`API ${configuredApiCount}/${cloud.branches.length} configured`}
                  dot={false}
                />
              )}
            </div>
          )}
          <DescriptionList
            columns={2}
            items={[
              {
                label: 'Cloud POS',
                value: <CloudPosBadge status={overview.cloudPosStatus ?? 'not_connected'} />,
              },
              {
                label: 'Last production refresh',
                value: overview.lastProductionRefreshAt
                  ? `${formatRelative(overview.lastProductionRefreshAt)} · ${formatDateTime(overview.lastProductionRefreshAt)}`
                  : '—',
              },
              ...connection.channels.map((ch) => ({
                label: ch.label,
                value:
                  ch.channel === 'cloud' ? (
                    ch.detail === 'Partially Connected' ? (
                      <span className="text-warning">Partially Connected</span>
                    ) : ch.state === 'connected' ? (
                      <span>
                        Connected{ch.detail ? <span className="text-ink-muted"> · {ch.detail}</span> : null}
                      </span>
                    ) : ch.state === 'unavailable' ? (
                      <span className="text-warning">Temporarily unavailable</span>
                    ) : (
                      <span className="text-ink-muted">Not Connected</span>
                    )
                  ) : ch.state === 'not_applicable' ? (
                    <span className="text-ink-muted">Not applicable</span>
                  ) : ch.state === 'unavailable' ? (
                    <span className="text-warning">Temporarily unavailable</span>
                  ) : ch.state === 'connected' ? (
                    <span>
                      Connected{ch.detail ? <span className="text-ink-muted"> · {ch.detail}</span> : null}
                    </span>
                  ) : (
                    <span className="text-ink-muted">Not Connected</span>
                  ),
              })),
            ]}
          />
          {connection.warnings.length > 0 && (
            <ul className="mt-4 space-y-1 rounded-md border border-warning/25 bg-warning-soft px-3 py-2 text-[13px] text-warning">
              {connection.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      )}

      {!hideLegacyBranchPanels && showTab(tab, 'branches', 'inventory') && (
      <SectionCard
        title="Cloud branches"
        icon={<Cloud size={16} aria-hidden="true" />}
        availability={cloud.availability}
        message={cloud.message}
        description={cloud.tenant ? cloud.tenant.name : undefined}
        actions={
          <div className="flex gap-1">
            {canTenant && cloud.tenant && (
              <Button variant="ghost" size="sm" leftIcon={<Pencil size={14} aria-hidden="true" />} onClick={() => setEditTenant(true)}>
                Edit tenant
              </Button>
            )}
            {canAddBranch && cloud.tenant && (
              <Button variant="ghost" size="sm" leftIcon={<Plus size={14} aria-hidden="true" />} onClick={() => setAddBranch(true)}>
                Add branch
              </Button>
            )}
          </div>
        }
      >
        {cloud.availability === 'not_applicable' || cloud.availability === 'not_linked' ? (
          <EmptyState
            title="Not Production-linked"
            description="Link or start Production setup before managing branch API keys."
            action={
              canAddBranch ? (
                <Button variant="secondary" size="sm" leftIcon={<Cloud size={14} aria-hidden="true" />} onClick={() => onRequestTab?.('overview')}>
                  Production setup
                </Button>
              ) : undefined
            }
          />
        ) : cloud.branches.length === 0 ? (
          <EmptyState
            title="No cloud branches"
            description="Add a Production branch under this chain to generate branch API keys."
            action={
              canAddBranch && cloud.tenant ? (
                <Button variant="primary" size="sm" leftIcon={<Plus size={14} aria-hidden="true" />} onClick={() => setAddBranch(true)}>
                  Add Branch
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line">
            {cloud.branches.map((b) => {
              const branchKeys = keysForBranch(api.keys, b.id);
              const apiConfigured = branchKeys.length > 0;
              const posCount = devices.pos.filter((d) => d.branchId === b.id).length;
              const syncStatus = b.estimatedOnlineStatus;
              return (
                <li key={b.id} className="px-3 py-2.5 text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-ink">
                        {b.name} <span className="font-mono text-xs text-ink-muted">({b.branchRef})</span>
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-secondary">
                        <CountryDisplay country={overview.country} city={overview.city} flagSize={12} />
                        <StatusBadge tone={b.isActive ? 'success' : 'neutral'} label={b.isActive ? 'Active' : 'Inactive'} />
                        {api.availability === 'available' && (
                          <StatusBadge
                            tone={apiConfigured ? 'success' : 'neutral'}
                            label={apiConfigured ? 'API Configured' : 'API Not Configured'}
                            dot={false}
                          />
                        )}
                        {devices.availability === 'available' && (
                          <StatusBadge
                            tone={posCount > 0 ? 'success' : 'neutral'}
                            label={posCount > 0 ? 'POS Connected' : 'POS Not Connected'}
                            dot={false}
                          />
                        )}
                        {syncStatus && (
                          <StatusBadge tone={onlineTone(syncStatus)} label={`Sync ${syncStatus}`} />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {canTenant && (
                        <Button variant="ghost" size="sm" onClick={() => setEditBranch(b)}>
                          Open
                        </Button>
                      )}
                      {api.availability === 'available' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (tab === 'branches') {
                              setInlineApiBranchId(b.id);
                              setFocusBranchApiId(b.id);
                            } else {
                              focusApi(b.id);
                            }
                          }}
                        >
                          API
                        </Button>
                      )}
                      {canEnroll && (
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={enrollmentBusy === b.id}
                          onClick={() => void revealEnrollment(b.id)}
                        >
                          Enrollment
                        </Button>
                      )}
                      {devices.availability === 'available' && (
                        <Button variant="ghost" size="sm" onClick={() => onRequestTab?.('access')}>
                          Devices
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => onRequestTab?.('sync')}>
                        Sync
                      </Button>
                      {canManageApi && !apiConfigured && productionAvailable && api.availability === 'available' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setInlineApiBranchId(b.id);
                            setFocusBranchApiId(b.id);
                          }}
                        >
                          Generate API
                        </Button>
                      )}
                      {canInventory && (
                        <Button variant="ghost" size="sm" leftIcon={<Package size={14} aria-hidden="true" />} onClick={() => setInventoryBranch(b)}>
                          Inventory
                        </Button>
                      )}
                    </div>
                  </div>
                  {inlineApiBranchId === b.id && tab === 'branches' && api.availability === 'available' && (
                    <div className="mt-2">
                      <BranchApiManager
                        businessId={bizId}
                        branchId={b.id}
                        branchName={b.name}
                        keys={branchKeys}
                        productionAvailable={productionAvailable}
                        focused={focusBranchApiId === b.id}
                        onChanged={() => onRefresh?.()}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {enrollmentError && <p className="mt-2 text-sm text-danger">{enrollmentError}</p>}
      </SectionCard>
      )}

      {showTab(tab, 'sync') && cloud.availability === 'available' && cloud.branches.length > 0 && (
        <SectionCard
          title="Sync Status"
          icon={<Wifi size={16} aria-hidden="true" />}
          availability={cloud.availability}
          message={cloud.message}
          description="ONLINE ≤10m · STALE ≤24h · OFFLINE otherwise"
        >
          <ul className="space-y-3">
            {cloud.branches.map((b) => (
              <li key={b.id} className="rounded-md border border-line px-4 py-3 text-sm">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="font-medium">{b.name}</span>
                  {b.estimatedOnlineStatus && <Badge tone={onlineTone(b.estimatedOnlineStatus)}>{b.estimatedOnlineStatus}</Badge>}
                </div>
                {b.sync ? (
                  <DescriptionList
                    columns={2}
                    items={[
                      {
                        label: 'Latest sync',
                        value: b.sync.latestSyncAt ? formatDateTime(b.sync.latestSyncAt) : '—',
                      },
                      { label: 'Sync status', value: b.sync.latestSyncStatus ?? '—' },
                      { label: 'Rows accepted', value: b.sync.rowsAccepted ?? '—' },
                      { label: 'Rows rejected', value: b.sync.rowsRejected ?? '—' },
                      { label: 'Active stock rows', value: b.sync.activeStockRows ?? '—' },
                      { label: 'Total stock rows', value: b.sync.totalStockRows ?? '—' },
                      {
                        label: 'Z-report last',
                        value: b.sync.zReportLastAt ? formatDateTime(b.sync.zReportLastAt) : '—',
                      },
                    ]}
                  />
                ) : (
                  <p className="text-xs text-ink-secondary">
                    Last sync {b.lastSyncedAt ? formatRelative(b.lastSyncedAt) : 'never'}
                    {typeof b.itemCount === 'number' ? ` · ${b.itemCount} items` : ''}
                    <span className="text-ink-muted"> · Detailed sync metrics unavailable for this branch</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {showTab(tab, 'pos-operation') && (
        <div className="xl:col-span-2">
          <PosControlManager details={details} mode="operation" />
        </div>
      )}

      {showTab(tab, 'pos-access') && (
        <div className="xl:col-span-2">
          <PosControlManager details={details} mode="access" />
        </div>
      )}

      {showTab(tab, 'license') && (
      <Card className="xl:col-span-2">
        <CardBody className="space-y-8">
          {licensing.availability === 'forbidden' ? (
            <EmptyState compact title="License details restricted" description={licensing.message ?? 'Your role cannot view Device Licenses.'} />
          ) : (
            <>
              <PharmacyLicenseManager
                businessId={bizId}
                businessName={overview.name}
                details={details}
                onRefresh={async () => {
                  onRefresh?.();
                }}
              />
              <div className="border-t border-line pt-6">
                <DeviceLicenseManager
                  businessId={bizId}
                  businessName={overview.name}
                  categoryKey={overview.category}
                  details={details}
                  onRefresh={async () => {
                    onRefresh?.();
                  }}
                />
              </div>
            </>
          )}
        </CardBody>
      </Card>
      )}

      {showTab(tab, 'access') && (
      <SectionCard title="Devices" icon={<MonitorSmartphone size={16} aria-hidden="true" />} availability={devices.availability} message={devices.message}>
        <div className="space-y-4">
          <DeviceGroup title="POS devices" empty="No cloud POS devices">
            {devices.pos.map((d) => (
              <li key={d.id} className="px-4 py-3 text-sm">
                <Badge tone="neutral" className="mb-1">
                  POS Device
                </Badge>
                <p className="font-medium">{d.deviceName || 'Unnamed device'}</p>
                <p className="font-mono text-xs text-ink-muted">{d.posDeviceUuid ?? '—'}</p>
                <p className="text-xs text-ink-secondary">{d.lastSeenAt ? `Last seen ${formatRelative(d.lastSeenAt)}` : 'No last seen'}</p>
              </li>
            ))}
          </DeviceGroup>
          <DeviceGroup title="Updater devices" empty="No updater devices">
            {devices.updater.map((d) => (
              <li key={d.id} className="px-4 py-3 text-sm">
                <Badge tone="neutral" className="mb-1">
                  Updater Device
                </Badge>
                <p className="font-medium">{d.nickname || d.deviceUuid}</p>
                <p className="font-mono text-xs text-ink-muted">{d.machineFingerprint}</p>
                <p className="text-xs text-ink-secondary">
                  v{d.currentVersion} · {d.channel} · {d.status}
                  {d.lastSeen ? ` · Last seen ${formatRelative(d.lastSeen)}` : ''}
                </p>
              </li>
            ))}
          </DeviceGroup>
          <DeviceGroup title="License hardware" empty="No license fingerprints">
            {devices.license.map((d) => (
              <li key={d.deviceId} className="px-4 py-3 text-sm">
                <Badge tone="neutral" className="mb-1">
                  License Device
                </Badge>
                <p className="font-mono text-xs">{d.deviceId}</p>
              </li>
            ))}
          </DeviceGroup>
        </div>
      </SectionCard>
      )}

      {showTab(tab, 'updates') && (
      <SectionCard title="Application / Updates" icon={<Download size={16} aria-hidden="true" />} availability={updates.availability} message={updates.message}>
        {updates.availability === 'not_linked' ? (
          <EmptyState title="Updater not linked" />
        ) : updates.customer ? (
          <DescriptionList
            items={[
              { label: 'Update customer', value: updates.customer.pharmacyName },
              { label: 'Channel', value: updates.customer.assignedChannel },
              { label: 'Status', value: updates.customer.status },
              {
                label: 'Last device seen',
                value: updates.customer.lastDeviceSeenAt ? formatRelative(updates.customer.lastDeviceSeenAt) : '—',
              },
              ...(updates.devices[0]
                ? [
                    { label: 'Installed version', value: updates.devices[0].currentVersion },
                    { label: 'Updater device', value: <span className="font-mono text-xs">{updates.devices[0].deviceUuid}</span> },
                  ]
                : []),
              ...(updates.clients[0]
                ? [
                    { label: 'Update-client version', value: updates.clients[0].currentVersion },
                    {
                      label: 'Update-client last seen',
                      value: updates.clients[0].lastSeenAt ? formatRelative(updates.clients[0].lastSeenAt) : '—',
                    },
                  ]
                : []),
            ]}
          />
        ) : (
          <p className="text-sm text-ink-muted">No updater customer detail available.</p>
        )}
      </SectionCard>
      )}

      {showTab(tab, 'owner') && (
      <SectionCard
        title="Owner Users"
        icon={<Smartphone size={16} aria-hidden="true" />}
        availability={owners.availability === 'forbidden' ? 'forbidden' : 'available'}
        message={owners.availability === 'forbidden' ? owners.message : null}
        description="Owner App credentials for the pharmacy owner. Access can be all branches or a single Production-mapped branch."
      >
        <OwnerUsersManager
          businessId={bizId}
          businessName={overview.name}
          details={details}
          onRefresh={async () => {
            onRefresh?.();
          }}
        />
      </SectionCard>
      )}

      {showTab(tab, 'access') && (
      <SectionCard
        title="Access & API"
        icon={<Cable size={16} aria-hidden="true" />}
        availability="available"
        message={
          api.availability === 'forbidden'
            ? 'API metadata restricted for your role — Generate / Reveal / Rotate require api.manage'
            : null
        }
        description="Each branch has its own qpos API. Stock is shared automatically across branches under the same Cloud POS account."
      >
        {(details.operationalBranches?.length ?? 0) === 0 ? (
          <EmptyState title="No branches" description="Add a branch first, then set up Cloud POS and generate its API." />
        ) : (
          <div className="space-y-3">
            {details.operationalBranches.map((row) => {
              const prodId = row.productionBranchId;
              if (row.cloudPos !== 'connected' || !prodId) {
                return (
                  <div key={row.id} className="rounded-md border border-line px-3 py-3">
                    <p className="text-sm font-medium text-ink">{row.name}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <StatusBadge tone="neutral" label="Cloud POS Not Connected" dot={false} />
                      <StatusBadge tone="neutral" label={`API ${BRANCH_API_STATE_LABELS[row.apiState]}`} dot={false} />
                      {onSetUpCloudPos && canTenant && (
                        <Button variant="secondary" size="sm" leftIcon={<Cloud size={14} aria-hidden="true" />} onClick={onSetUpCloudPos}>
                          {overview.cloudPosStatus === 'not_connected' ? 'Set Up Cloud POS' : 'Connect to Cloud POS'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <BranchApiManager
                  key={row.id}
                  businessId={bizId}
                  branchId={prodId}
                  branchName={row.name}
                  keys={keysForBranch(api.keys, prodId)}
                  productionAvailable={productionAvailable}
                  focused={focusBranchApiId === prodId}
                  onChanged={() => onRefresh?.()}
                />
              );
            })}
            <p className="text-[12px] text-ink-muted">
              Copy the API into the Point of Sale for that branch. Enrollment is optional and not required for stock sync.
            </p>
          </div>
        )}
      </SectionCard>
      )}

      {showTab(tab, 'overview') && (
      <Card className="xl:col-span-2">
        <CardHeader title="Identity history" icon={<History size={16} aria-hidden="true" />} description="Confirmed, proposed, retired and rejected links" />
        <CardBody>
          {identityHistory.links.length === 0 ? (
            <EmptyState title="No identity links" />
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line">
              {identityHistory.links.map((l) => (
                <li key={l.id} className="px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{ENTITY_TYPE_LABELS[l.entityType]}</span>
                    <Badge tone="neutral">{l.status}</Badge>
                    <Badge tone="neutral">{l.role}</Badge>
                    <Badge tone="neutral">{l.confidence}</Badge>
                    <span className="text-xs text-ink-muted">{SYSTEM_LABELS[l.system]}</span>
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium text-ink-secondary hover:text-ink">Technical details</summary>
                    <p className="mt-1 font-mono text-xs text-ink-muted" title={l.externalId}>
                      {l.externalId}
                      {l.displayLabel ? ` · ${l.displayLabel}` : ''}
                    </p>
                    <p className="mt-0.5 font-mono text-[11px] text-ink-muted">Masked: {maskId(l.externalId)}</p>
                  </details>
                  {l.role === 'historical' && (
                    <p className="mt-1 text-xs text-ink-secondary">Historical — not used for live operational status</p>
                  )}
                  {l.status === 'proposed' && (
                    <p className="mt-1 text-xs text-ink-secondary">Proposed — awaiting confirmation</p>
                  )}
                  {l.warnings.map((w) => (
                    <p key={w} className="mt-1 text-xs text-warning">
                      {w}
                    </p>
                  ))}
                  {l.evidenceSummary[0] && <p className="mt-1 text-xs text-ink-muted">{l.evidenceSummary[0]}</p>}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      )}

      {showTab(tab, 'activity') && activity.availability === 'available' && activity.events.length > 0 && (
        <Card className="xl:col-span-2">
          <CardHeader title="Activity" icon={<History size={16} aria-hidden="true" />} />
          <CardBody>
            <ul className="space-y-2">
              {activity.events.map((e) => (
                <li key={e.id} className="text-sm">
                  <span className="text-ink-muted">{formatDateTime(e.occurredAt)}</span>
                  <span className="mx-2 text-ink-muted">·</span>
                  <span className="text-ink">{e.summary}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {showTab(tab, 'activity') && (activity.availability !== 'available' || activity.events.length === 0) && tab === 'activity' && (
        <Card className="xl:col-span-2">
          <CardHeader title="Activity" icon={<History size={16} aria-hidden="true" />} />
          <CardBody>
            <EmptyState title="No recent production activity" description="Central Admin activity appears in the Activity tab when available." />
          </CardBody>
        </Card>
      )}

      {enrollment && (
        <Modal
          open
          title="Enrollment code"
          onClose={() => setEnrollment(null)}
          size="md"
          footer={<Button onClick={() => setEnrollment(null)}>Done</Button>}
        >
          <SecretField
            label="Enrollment code"
            value={enrollment.code}
            initiallyRevealed
            notRecoverable
            onClearOnUnmount={() => setEnrollment(null)}
            onHide={() => setEnrollment(null)}
          />
        </Modal>
      )}

      {inventoryBranch && (
        <CloudInventoryModal
          businessId={bizId}
          branch={inventoryBranch}
          onClose={() => setInventoryBranch(null)}
        />
      )}

      {editTenant && cloud.tenant && (
        <EditTenantModal
          businessId={bizId}
          initialName={cloud.tenant.name}
          initialBusinessType={cloud.tenant.businessType}
          onClose={() => setEditTenant(false)}
          onDone={() => {
            setEditTenant(false);
            onRefresh?.();
          }}
        />
      )}

      {editBranch && (
        <EditBranchModal
          businessId={bizId}
          branch={editBranch}
          onClose={() => setEditBranch(null)}
          onDone={() => {
            setEditBranch(null);
            onRefresh?.();
          }}
        />
      )}

      {addBranch && (
        <AddProductionBranchModal
          businessId={bizId}
          onClose={() => setAddBranch(false)}
          onDone={() => {
            setAddBranch(false);
            onRefresh?.();
          }}
          onGoGenerateApi={() => {
            onRequestTab?.('access');
          }}
          onGoEnrollment={() => {
            onRequestTab?.('branches');
          }}
        />
      )}
    </>
  );
}

function DeviceGroup({ title, empty, children }: { title: string; empty: string; children: ReactNode[] }) {
  const items = children.filter(Boolean);
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">{empty}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">{items}</ul>
      )}
    </div>
  );
}

function CloudInventoryModal({
  businessId,
  branch,
  onClose,
}: {
  businessId: string;
  branch: LiveBranchRow;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<
    Array<{
      sku?: string;
      barcode?: string | null;
      itemName?: string;
      name?: string;
      price?: number | string | null;
      stock?: number | string | null;
      quantity?: number | string | null;
    }>
  >([]);
  const [total, setTotal] = useState<number | null>(null);

  const load = async (nextPage = page, nextQ = q) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listBranchInventory(businessId, branch.id, {
        page: nextPage,
        limit: 50,
        q: nextQ.trim() || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
      setPage(nextPage);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when the modal opens for this branch
  }, [businessId, branch.id]);

  return (
    <Modal
      open
      title={`Cloud inventory — ${branch.name}`}
      onClose={onClose}
      size="lg"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <p className="mb-3 text-xs text-ink-muted">
        Read-only. POS sync is the source of truth. Inventory CRUD / import / purge are retired in Central Admin.
      </p>
      <div className="mb-3 flex gap-2">
        <SearchInput
          label="Search inventory"
          value={q}
          onChange={setQ}
          placeholder="Search SKU, barcode, name…"
          className="flex-1"
        />
        <Button
          variant="secondary"
          leftIcon={<RefreshCw size={14} aria-hidden="true" />}
          loading={loading}
          onClick={() => void load(1, q)}
        >
          Search
        </Button>
      </div>
      {error && <p className="mb-2 text-sm text-danger">{error}</p>}
      {loading && items.length === 0 ? (
        <LoadingState className="py-8" label="Loading inventory…" />
      ) : items.length === 0 ? (
        <EmptyState title="No inventory rows" description="No items matched, or the branch has not synced yet." />
      ) : (
        <TableContainer>
          <Table>
            <THead>
              <TR>
                <TH>SKU</TH>
                <TH>Name</TH>
                <TH>Barcode</TH>
                <TH>Stock</TH>
                <TH>Price</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((item, i) => (
                <TR key={`${item.sku ?? i}-${item.barcode ?? ''}`}>
                  <TD className="font-mono text-xs">{item.sku ?? '—'}</TD>
                  <TD>{item.itemName || item.name || '—'}</TD>
                  <TD className="font-mono text-xs">{item.barcode ?? '—'}</TD>
                  <TD className="tabular-nums">{item.stock ?? item.quantity ?? '—'}</TD>
                  <TD className="tabular-nums">{item.price ?? '—'}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      )}
      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-ink-muted">{total != null ? `${total} total` : `Page ${page}`}</p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1 || loading} onClick={() => void load(page - 1, q)}>
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={loading || (total != null ? page * 50 >= total : items.length < 50)}
            onClick={() => void load(page + 1, q)}
          >
            Next
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditTenantModal({
  businessId,
  initialName,
  initialBusinessType,
  onClose,
  onDone,
}: {
  businessId: string;
  initialName: string;
  initialBusinessType: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [businessType, setBusinessType] = useState(initialBusinessType);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateProductionTenant(businessId, {
        name: name.trim(),
        businessType: businessType.trim(),
        idempotencyKey: crypto.randomUUID(),
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Edit Production tenant"
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={busy} disabled={!name.trim() || !businessType.trim()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Name" htmlFor="prod-tenant-name" required>
          <Input id="prod-tenant-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
        </FormField>
        <FormField label="Business type" htmlFor="prod-tenant-type" required>
          <Input id="prod-tenant-type" value={businessType} onChange={(e) => setBusinessType(e.target.value)} maxLength={80} />
        </FormField>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function EditBranchModal({
  businessId,
  branch,
  onClose,
  onDone,
}: {
  businessId: string;
  branch: LiveBranchRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(branch.name);
  const [branchRef, setBranchRef] = useState(branch.branchRef);
  const [isActive, setIsActive] = useState(branch.isActive);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateProductionBranch(businessId, branch.id, {
        name: name.trim(),
        branchRef: branchRef.trim().toUpperCase(),
        isActive,
        idempotencyKey: crypto.randomUUID(),
      });
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Edit Production branch"
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={busy} disabled={!name.trim() || !branchRef.trim()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Name" htmlFor="prod-branch-name" required>
          <Input id="prod-branch-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
        </FormField>
        <FormField
          label="Branch ID"
          htmlFor="prod-branch-ref"
          required
          hint="Must match the Branch ID configured in the pharmacy POS."
        >
          <Input
            id="prod-branch-ref"
            value={branchRef}
            onChange={(e) => setBranchRef(e.target.value.toUpperCase())}
            maxLength={16}
            placeholder="BR-01"
            className="font-mono"
          />
        </FormField>
        <Checkbox label="Active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function AddProductionBranchModal({
  businessId,
  onClose,
  onDone,
  onGoGenerateApi,
  onGoEnrollment,
}: {
  businessId: string;
  onClose: () => void;
  onDone: () => void;
  onGoGenerateApi?: () => void;
  onGoEnrollment?: () => void;
}) {
  const [name, setName] = useState('');
  const [branchRef, setBranchRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);
  const [enrollmentCode, setEnrollmentCode] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await createProductionBranch(businessId, {
        branch: { branchRef: branchRef.trim().toUpperCase(), name: name.trim(), primary: true },
        // Do not auto-generate an API key — operator chooses next steps.
        createApiKeyForFirstBranch: false,
      });
      const code = res.secrets?.enrollmentCodes?.[0]?.enrollmentCode ?? null;
      setEnrollmentCode(code);
      setCreated(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Add Production branch"
      onClose={onClose}
      size="md"
      footer={
        created ? (
          <Button
            onClick={() => {
              onDone();
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={busy} disabled={!name.trim() || !branchRef.trim()}>
              Create branch
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-4">
          <p className="text-sm text-ink-secondary">
            Branch created. No API key was generated automatically — choose a next step when ready.
          </p>
          {enrollmentCode != null && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-ink-secondary">Enrollment code (shown once)</p>
              <p className="rounded-md border border-line bg-surface-sunken px-3 py-3 font-mono text-lg tracking-wide">{enrollmentCode}</p>
            </div>
          )}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Next steps</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  onDone();
                  onGoGenerateApi?.();
                }}
              >
                Generate API
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  onDone();
                  onGoEnrollment?.();
                }}
              >
                Enrollment
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDone()}>
                Done
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <FormField label="Branch name" htmlFor="add-branch-name" required>
            <Input id="add-branch-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </FormField>
          <FormField
            label="Branch ID"
            htmlFor="add-branch-ref"
            required
            hint="Must match the Branch ID configured in the pharmacy POS."
          >
            <Input
              id="add-branch-ref"
              value={branchRef}
              onChange={(e) => setBranchRef(e.target.value.toUpperCase())}
              maxLength={16}
              placeholder="BR-01"
              className="font-mono"
            />
          </FormField>
          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
